import { Box, Button, Dialog, Typography } from '@mui/material';
import colors from 'assets/scss/_themes-vars.module.scss';
import iconInfo from 'assets/images/icons/icon_info.svg';
import { geneRateDate } from 'constant/excelFileName';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { useCallback, useState } from 'react';
import * as XLSX from 'xlsx';

export interface IDuplicateStaffConflict {
  staffID?: string;
  staffName?: string;
  email?: string;
  userName?: string;
  conflictSurveyID?: string;
  conflictSurveyName?: string;
  conflictStartDate?: string;
  conflictEndDate?: string;
}

export interface IDuplicateStaffErrorResponse {
  message?: string;
  statusCode?: number;
  errorCode?: number;
  duplicateStaffs?: IDuplicateStaffConflict[];
}

type DuplicateStaffModalState = {
  isOpen: boolean;
  message: string;
  messagePrefix: string;
  messageSuffix: string;
  usernamesText: string;
  duplicateStaffs: IDuplicateStaffConflict[];
};

const DUPLICATE_STAFF_ERROR_CODES = [1];
const MAX_USERNAME_IN_MESSAGE = 3;
const DEFAULT_DUPLICATE_MESSAGE_RELEASE =
  'Không thể phát hành do có thành viên thuộc bài khảo sát khác trong khoảng thời gian này';
const DEFAULT_DUPLICATE_MESSAGE =
  'Không lưu được danh sách thành viên do có thành viên thuộc bài khảo sát khác trong khoảng thời gian này';

const buildUsernamesText = (duplicateStaffs: IDuplicateStaffConflict[]) => {
  const hasMoreDuplicateStaffs = duplicateStaffs.length > MAX_USERNAME_IN_MESSAGE;
  const userNames = duplicateStaffs
    .map((staff) => staff.userName?.toLowerCase())
    .filter((userName): userName is string => Boolean(userName?.trim()));

  const displayUserNames = userNames
    .slice(0, MAX_USERNAME_IN_MESSAGE)
    .map((userName) => userName)
    .join(', ');

  const suffix = hasMoreDuplicateStaffs ? ',...' : '';
  return `${displayUserNames}${suffix}`;
};

const buildMessage = (duplicateStaffs: IDuplicateStaffConflict[], isRelease?: boolean) => {
  const usernamesText = buildUsernamesText(duplicateStaffs);

  if (isRelease) {
    if (!usernamesText) {
      return {
        message: DEFAULT_DUPLICATE_MESSAGE_RELEASE,
        messagePrefix: '',
        messageSuffix: '',
        usernamesText: '',
      };
    }

    return {
      message: '',
      messagePrefix: 'Không thể phát hành do có thành viên: ',
      usernamesText,
      messageSuffix: ' thuộc bài khảo sát Gương thần khác trong khoảng thời gian này',
    };
  } else {
    if (!usernamesText) {
      return {
        message: DEFAULT_DUPLICATE_MESSAGE,
        messagePrefix: '',
        messageSuffix: '',
        usernamesText: '',
      };
    }

    return {
      message: '',
      messagePrefix: 'Không lưu được danh sách thành viên do có thành viên: ',
      usernamesText,
      messageSuffix: ' thuộc bài khảo sát Gương thần khác trong khoảng thời gian này',
    };
  }
};

const useDuplicateStaffConflictModal = () => {
  const [modalState, setModalState] = useState<DuplicateStaffModalState>({
    isOpen: false,
    message: '',
    messagePrefix: '',
    messageSuffix: '',
    usernamesText: '',
    duplicateStaffs: [],
  });

  const handleCloseModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const handleDownloadDuplicateStaffsExcel = useCallback(() => {
    if (!modalState.duplicateStaffs.length) {
      return;
    }

    const headerRow = ['Username', 'Họ Tên', 'Email', 'Tên bài đánh giá'];
    const dataRows = modalState.duplicateStaffs.map((staff) => [
      staff.userName?.toLowerCase() || '',
      staff.staffName || '',
      staff.email || '',
      staff.conflictSurveyName || '',
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Danh sach');
    XLSX.writeFile(workbook, `Danh_sach_thanh_vien_trung_${geneRateDate()}.xlsx`);
  }, [modalState.duplicateStaffs]);

  const handleDuplicateStaffError = useCallback(
    (response?: IDuplicateStaffErrorResponse | null, isRelease?: boolean): boolean => {
      if (!response) {
        return false;
      }

      const duplicateStaffs = Array.isArray(response.duplicateStaffs)
        ? response.duplicateStaffs
        : [];
      const errorCode = Number(response.errorCode);
      const isDuplicateStaffError =
        response.statusCode === 400 && DUPLICATE_STAFF_ERROR_CODES.includes(errorCode);

      if (!isDuplicateStaffError) {
        return false;
      }
      const messageData = buildMessage(duplicateStaffs, isRelease);

      setModalState({
        isOpen: true,
        ...messageData,
        duplicateStaffs,
      });

      return true;
    },
    []
  );

  const shouldShowDownloadLink = modalState.duplicateStaffs.length > MAX_USERNAME_IN_MESSAGE;

  const duplicateStaffConflictModal = (
    <Dialog
      open={modalState.isOpen}
      onClose={handleCloseModal}
      maxWidth="xs"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '500px',
          maxWidth: 'calc(100% - 32px)',
          borderRadius: '14px',
          p: '18px 22px 16px',
          backgroundColor: colors.grey200,
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          component="img"
          src={iconInfo}
          alt="icon-info"
          sx={{
            width: '40px',
            height: '40px',
            mb: 2.25,
          }}
        />

        <Typography
          sx={{
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: 400,
            lineHeight: '28px',
            color: '#333333',
            px: 1.5,
            mb: shouldShowDownloadLink ? 3 : 0,
          }}
        >
          {modalState.usernamesText ? (
            <>
              {modalState.messagePrefix}
              <Box component="span" sx={{ color: '#E4B653' }}>
                {modalState.usernamesText}
              </Box>
              {modalState.messageSuffix}
            </>
          ) : (
            modalState.message
          )}
        </Typography>

        <Box
          component="button"
          type="button"
          onClick={handleDownloadDuplicateStaffsExcel}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            border: 'none',
            background: 'transparent',
            p: 0,
            pt: 1,
            color: '#1686EC',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: '16px',
            lineHeight: '20px',
            mb: '2px',
            '&:hover': {
              opacity: 0.9,
            },
          }}
        >
          <LinkOutlinedIcon sx={{ fontSize: '16px' }} />
          Danh sách thành viên
        </Box>

        <Button
          onClick={handleCloseModal}
          sx={{
            mt: shouldShowDownloadLink ? 2.25 : 3,
            height: '42px',
            width: '100%',
            borderRadius: '8px',
            color: colors.paper,
            backgroundColor: '#49C24E',
            fontSize: '18px',
            fontWeight: 700,
            lineHeight: '20px',
            textTransform: 'uppercase',
            boxShadow: '0 6px 0 #2E8F38',
            '&:hover': {
              backgroundColor: '#49C24E',
              opacity: 0.9,
            },
          }}
        >
          ĐÓNG
        </Button>
      </Box>
    </Dialog>
  );

  return {
    handleDuplicateStaffError,
    duplicateStaffConflictModal,
  };
};

export default useDuplicateStaffConflictModal;
